import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faHourglassHalf, faLocationDot, faCamera, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { coralSelectStyles, coralSelectTheme } from './coralSelect';
import TaskListEditor from './TaskListEditor';
import IncludedSet, { standardLinenItems } from './IncludedSet';
import './clientRequests.css';
import { AREA_OPTIONS } from '../../../constants/areas';
import { formatAreaOption } from '../../../constants/areaOption';

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const START_POS = { lat: 25.2048, lng: 55.2708 }; // Dubai
const DRAFTS_URL = '/api/client-requests/requests/properties/drafts';

// Same area options as the admin property form

const BEDROOM_OPTIONS = [
    { value: 0, label: 'Studio' },
    ...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: n, label: `${n} Bedroom${n > 1 ? 's' : ''}` })),
];

// Half steps = bathroom + guest toilet (e.g. 1.5).
const BATHROOM_OPTIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8]
    .map((n) => ({ value: n, label: `${n} Bathroom${n > 1 ? 's' : ''}` }));
const BED_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ value: n, label: String(n) }));

// The five steps (owner, 2026-09-14). Step 1 done = a draft exists.
const STEPS = [
    { n: 1, title: 'Location', short: 'Location' },
    { n: 2, title: 'Property Layout', short: 'Layout' },
    { n: 3, title: 'Additional Amenities & Services', short: 'Amenities' },
    { n: 4, title: 'Setup & Tasks', short: 'Setup & Tasks' },
    { n: 5, title: 'Notes', short: 'Notes' },
];
const LAST_STEP = STEPS.length;

const isValidCoord = (n) => Number.isFinite(n) && Math.abs(n) > 0.000001;

/* --- Building search (owner, 2026-09-14) ---
   Our own input over the server-side Places proxy the app already uses
   (GET /requests/places/autocomplete + /details), so the field looks and
   focuses like every other field here — Google's widget brought its own
   blue focus ring that could not be restyled from outside its shadow root. */
const PLACES_URL = '/api/client-requests/requests/places';
const newSessionToken = () => (
    window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

function PlacesSearch({ onPick, initialText = '' }) {
    const [text, setText] = useState(initialText);
    const [predictions, setPredictions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const sessionRef = useRef(newSessionToken());
    const boxRef = useRef(null);
    // The text a pick just wrote into the box: no new search for it.
    const pickedRef = useRef(initialText);

    // Debounced lookup; one Places session per pick keeps Google's billing sane.
    useEffect(() => {
        const q = text.trim();
        if (q.length < 2 || q === pickedRef.current.trim()) { setPredictions([]); return undefined; }
        let cancelled = false;
        setSearching(true);
        const handle = setTimeout(async () => {
            try {
                const res = await axios.get(`${PLACES_URL}/autocomplete`, { params: { input: q, sessiontoken: sessionRef.current } });
                if (!cancelled) setPredictions(Array.isArray(res.data?.predictions) ? res.data.predictions : []);
            } catch (err) {
                if (!cancelled) {
                    setPredictions([]);
                    if (err.response?.status === 503) setUnavailable(true);
                }
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [text]);

    // A click anywhere else closes the list.
    useEffect(() => {
        const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setPredictions([]); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    const pick = async (p) => {
        pickedRef.current = p.description;
        setText(p.description);
        setPredictions([]);
        try {
            const res = await axios.get(`${PLACES_URL}/details`, { params: { place_id: p.place_id, sessiontoken: sessionRef.current } });
            sessionRef.current = newSessionToken(); // consumed by details
            const d = res.data || {};
            if (d.latitude != null && d.longitude != null) {
                onPick({
                    lat: Number(d.latitude),
                    lng: Number(d.longitude),
                    placeId: d.place_id || p.place_id || null,
                    // The prediction names the building; details only give the street.
                    placeDesc: p.description || d.description || '',
                    mapsUri: d.maps_link || null,
                    name: d.name || '',
                });
            }
        } catch (err) {
            console.error('Place details failed:', err);
        }
    };

    return (
        <div className="creq-places" ref={boxRef}>
            <div className="creq-places-field">
                <FontAwesomeIcon icon={faLocationDot} className="creq-places-pin" />
                <input
                    type="text"
                    className="creq-places-input"
                    placeholder={unavailable ? 'Search unavailable — tap the map to pin your building' : 'Search your building or address'}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoComplete="off"
                    disabled={unavailable}
                    aria-label="Search your building or address"
                />
                {searching ? (
                    <span className="creq-places-spinner" aria-label="Searching" />
                ) : text && !unavailable ? (
                    <button
                        type="button"
                        className="creq-places-clear"
                        onClick={() => { setText(''); setPredictions([]); }}
                        title="Clear"
                        aria-label="Clear search"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                ) : null}
            </div>
            {predictions.length > 0 && (
                <div className="creq-places-list" role="listbox">
                    {predictions.map((p) => (
                        <button type="button" key={p.place_id} className="creq-places-item" onClick={() => pick(p)} role="option" aria-selected={false}>
                            <FontAwesomeIcon icon={faLocationDot} className="creq-places-item-icon" />
                            <span>{p.description}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function MapClick({ onPick }) {
    const map = useMap();
    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('click', (e) => {
            const lat = e.latLng?.lat?.();
            const lng = e.latLng?.lng?.();
            if (typeof lat === 'number' && typeof lng === 'number') onPick({ lat, lng });
        });
        return () => listener?.remove?.();
    }, [map, onPick]);
    return null;
}

function PanTo({ position, zoom = 16 }) {
    const map = useMap();
    useEffect(() => {
        if (!map || !position) return;
        map.panTo(position);
        map.setZoom(zoom);
    }, [map, position, zoom]);
    return null;
}

function Toggle({ checked, onChange, label, hint, className = '' }) {
    return (
        <button
            type="button"
            className={`creq-toggle ${checked ? 'is-on' : ''} ${className}`}
            onClick={() => onChange(!checked)}
        >
            <span className="creq-toggle-track"><span className="creq-toggle-thumb" /></span>
            <span className="creq-toggle-text">
                {label}
                {hint && <span className="creq-toggle-hint">{hint}</span>}
            </span>
        </button>
    );
}

const EMPTY_FORM = {
    address_line_1: '',
    unit_number: '',
    area: '',
    google_maps_link: '',
    latitude: null,
    longitude: null,
    google_place_id: null,
    google_place_desc: '',
    bedrooms: 0, // default to Studio — clients scale up from the smallest unit
    bathrooms: 1,
    single_beds: 0,
    double_beds: 1,
    super_king_beds: 0,
    required_cleaners: 1,
    is_beach_towel: false,
    is_dolce_gusto: false,
    is_nespresso: false,
    property_access: '',
    wifi_name: '',
    wifi_password: '',
    notes: '',
};

// A saved draft's payload back into form state (same keys, server-normalised).
function formFromPayload(p) {
    if (!p) return EMPTY_FORM;
    const num = (v, d) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? d : Number(v));
    return {
        ...EMPTY_FORM,
        address_line_1: p.address_line_1 || '',
        unit_number: p.unit_number || '',
        area: p.area || '',
        google_maps_link: p.google_maps_link || '',
        latitude: p.latitude ?? null,
        longitude: p.longitude ?? null,
        google_place_id: p.google_place_id || null,
        google_place_desc: p.google_place_desc || '',
        bedrooms: num(p.bedrooms, 0),
        bathrooms: num(p.bathrooms, 1),
        single_beds: num(p.single_beds, 0),
        double_beds: num(p.double_beds, 1),
        super_king_beds: num(p.super_king_beds, 0),
        required_cleaners: num(p.required_cleaners, 1),
        is_beach_towel: !!p.is_beach_towel,
        is_dolce_gusto: !!p.is_dolce_gusto,
        is_nespresso: !!p.is_nespresso,
        property_access: p.property_access || '',
        wifi_name: p.wifi_name || '',
        wifi_password: p.wifi_password || '',
        notes: p.notes || '',
    };
}

/**
 * Add a property in FIVE STEPS (owner, 2026-09-14): Location → Property
 * Layout → Additional Amenities & Services → Setup & Tasks → Notes.
 *
 * DRAFT AFTER STEP 1. Finishing the location saves the request as a DRAFT
 * (POST /properties/drafts); every later step saves onto that same draft
 * (PUT), and closing the dialog keeps it — the properties page lists it as
 * "Draft" with a Continue button. Only the last step's "Submit for review"
 * turns it into a pending request staff can see. Pass `draft` (a row from
 * GET /requests/my?status=draft) to reopen one where the client left off.
 *
 * PRICE FROM STEP 2. The All-Inclusive calculator docks under the form from
 * the layout step on and re-prices as the layout and amenities change.
 *
 * Access and WiFi are optional — the cleaner can ask; a missing password
 * must never block a property (owner, 2026-09-14).
 */
export default function RequestPropertyModal({ draft = null, onClose, onSaved, onSubmitted }) {
    const payload = draft?.payload || null;
    const [form, setForm] = useState(() => formFromPayload(payload));
    // Uploaded the moment they are picked, so a draft keeps its photos:
    // [{ key, file, previewUrl, file_key, file_url, description, make_task, uploading, failed }]
    const [photos, setPhotos] = useState(() => (Array.isArray(payload?.setup_photos) ? payload.setup_photos : [])
        .map((p, i) => ({
            key: `saved-${i}-${p.file_key}`,
            file: null,
            previewUrl: p.file_url,
            file_key: p.file_key,
            file_url: p.file_url,
            description: p.description || '',
            make_task: !!p.make_task,
            uploading: false,
            failed: false,
        })));
    const [tasks, setTasks] = useState(() => (Array.isArray(payload?.property_tasks) ? payload.property_tasks : [])
        .map((t) => ({ id: null, instruction: t.instruction || '', is_report_required: !!t.is_report_required })));
    // Multi-billing (PLAN_multi_billing.md): which entity the new property is
    // billed to. Empty list / single entry → the picker never renders and the
    // submit body is untouched (non-multi clients see zero change).
    const [billingEntities, setBillingEntities] = useState([]);
    const [billedTo, setBilledTo] = useState(payload?.billing_profile_id ? String(payload.billing_profile_id) : ''); // '' = default entity
    const [dragOver, setDragOver] = useState(false);
    const [price, setPrice] = useState(null);
    const [priceLoading, setPriceLoading] = useState(false);
    // Reopen where the client left off: the step after the furthest one saved.
    const [step, setStep] = useState(() => Math.min(LAST_STEP, (Number(payload?.draft_step) || 0) + 1));
    const [draftId, setDraftId] = useState(draft?.id || null);
    const [saving, setSaving] = useState(false);
    // Set when Continue is pressed with the step incomplete: only then do the
    // "required" hints show (owner, 2026-09-14: not before they tried).
    const [attempted, setAttempted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const fileInputRef = useRef(null);
    const bodyRef = useRef(null);
    // Uploads that were still running when the dialog closed: their results
    // land here so the close-save (and the next open) still gets them.
    const uploadsRef = useRef({ pending: [], done: {} });
    // Furthest step completed — what the draft remembers.
    const furthestRef = useRef(Number(payload?.draft_step) || 0);
    // Latest state for the fire-and-forget save on close.
    const latestRef = useRef({});

    const set = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);

    // Every dropdown here: coral styling, the menu in a fixed-position portal
    // above the modal (so it never stretches the dialog or scrolls the form
    // to reveal itself), opening upward when the screen runs out below.
    const selectProps = {
        styles: coralSelectStyles,
        theme: coralSelectTheme,
        menuPortalTarget: document.body,
        menuPosition: 'fixed',
        menuPlacement: 'auto',
        menuShouldScrollIntoView: false,
        maxMenuHeight: 240,
    };

    // A new step always opens at its top, never wherever the last one was
    // scrolled to (owner, 2026-09-14).
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [step]);

    const position = useMemo(() => (
        isValidCoord(Number(form.latitude)) && isValidCoord(Number(form.longitude))
            ? { lat: Number(form.latitude), lng: Number(form.longitude) }
            : null
    ), [form.latitude, form.longitude]);

    // What the map filled in last, so a later pick may replace it but never
    // something the client typed themselves.
    const autoFilledRef = useRef({ address: '', area: '' });

    // The pin decides the area: the same zone lookup the office forms use.
    const suggestArea = useCallback(async (lat, lng) => {
        try {
            const res = await axios.get('/api/areas/suggest', { params: { lat, lng } });
            const suggestion = res.data?.suggestion;
            if (!suggestion || !AREA_OPTIONS.some((o) => o.value === suggestion)) return;
            setForm((prev) => {
                if (prev.area && prev.area !== autoFilledRef.current.area) return prev;
                autoFilledRef.current.area = suggestion;
                return { ...prev, area: suggestion };
            });
        } catch (err) {
            // No suggestion is fine — the client picks the area themselves.
        }
    }, []);

    const handlePlacePick = useCallback(({ lat, lng, placeId, placeDesc, mapsUri, name }) => {
        setForm((prev) => {
            const next = {
                ...prev,
                latitude: lat,
                longitude: lng,
                google_place_id: placeId,
                google_place_desc: placeDesc || prev.google_place_desc,
                google_maps_link: mapsUri || `https://maps.google.com/?q=${lat},${lng}`,
            };
            // The place's own name becomes the building name unless the
            // client already typed one.
            if (name && (!prev.address_line_1.trim() || prev.address_line_1 === autoFilledRef.current.address)) {
                next.address_line_1 = name.slice(0, 120);
                autoFilledRef.current.address = next.address_line_1;
            }
            return next;
        });
        suggestArea(lat, lng);
    }, [suggestArea]);

    const handleMapPick = useCallback(({ lat, lng }) => {
        setForm((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            // Manual pin: any previously searched place no longer describes
            // this location — clear BOTH the id and the description.
            google_place_id: null,
            google_place_desc: '',
            google_maps_link: `https://maps.google.com/?q=${lat},${lng}`,
        }));
        suggestArea(lat, lng);
    }, [suggestArea]);

    // Billing entities — only multi-billing clients get more than one back;
    // a failure just leaves the picker hidden (default entity applies).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get('/api/client-requests/requests/billing-entities');
                if (!cancelled) setBillingEntities(Array.isArray(res.data?.entities) ? res.data.entities : []);
            } catch (err) {
                if (!cancelled) setBillingEntities([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const billingOptions = useMemo(() => billingEntities.map((e) => ({
        value: e.billing_profile_id == null ? '' : String(e.billing_profile_id),
        label: e.is_default ? `${e.label} (Default)` : e.label,
    })), [billingEntities]);

    // Live price calculation (server-side, debounced).
    // Linen hire is always included for client properties.
    useEffect(() => {
        setPriceLoading(true);
        const handle = setTimeout(async () => {
            try {
                const res = await axios.post('/api/client-requests/requests/properties/price-preview', {
                    bedrooms: form.bedrooms,
                    single_beds: form.single_beds,
                    double_beds: form.double_beds,
                    super_king_beds: form.super_king_beds,
                    required_cleaners: form.required_cleaners,
                    is_linen_hire: true,
                    is_beach_towel: form.is_beach_towel,
                    is_dolce_gusto: form.is_dolce_gusto,
                    is_nespresso: form.is_nespresso,
                });
                setPrice(res.data?.price || null);
            } catch (err) {
                console.error('Price preview failed:', err);
                setPrice(null);
            } finally {
                setPriceLoading(false);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [
        form.bedrooms, form.single_beds, form.double_beds, form.super_king_beds, form.required_cleaners,
        form.is_beach_towel, form.is_dolce_gusto, form.is_nespresso,
    ]);

    /* ── Photos: uploaded on pick so the draft can keep them ── */
    const uploadPhoto = async (photo) => {
        const res = await axios.post('/api/client-requests/requests/properties/upload-photo-url', {
            fileName: photo.file.name,
            fileType: photo.file.type,
        });
        const { url, fileKey, fileUrl } = res.data;
        await fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': photo.file.type,
                'x-amz-acl': 'public-read',
            },
            body: photo.file,
        });
        return { file_key: fileKey, file_url: fileUrl };
    };

    const addPhotoFiles = (files) => {
        const images = files.filter((f) => f.type.startsWith('image/'));
        const fresh = images.slice(0, Math.max(0, 10 - photos.length)).map((file, i) => ({
            key: `${Date.now()}-${i}-${file.name}`,
            file,
            previewUrl: URL.createObjectURL(file),
            file_key: null,
            file_url: null,
            description: '',
            make_task: false,
            uploading: true,
            failed: false,
        }));
        if (!fresh.length) return;
        setPhotos((prev) => [...prev, ...fresh]);
        fresh.forEach((photo) => {
            const job = (async () => {
                try {
                    const up = await uploadPhoto(photo);
                    uploadsRef.current.done[photo.key] = up;
                    setPhotos((prev) => prev.map((p) => (p.key === photo.key ? { ...p, ...up, uploading: false } : p)));
                } catch (err) {
                    console.error('Photo upload failed:', err);
                    setPhotos((prev) => prev.map((p) => (p.key === photo.key ? { ...p, uploading: false, failed: true } : p)));
                }
            })();
            uploadsRef.current.pending.push(job);
        });
    };

    const togglePhotoTask = (key) => {
        setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, make_task: !p.make_task } : p)));
    };

    const handlePickPhotos = (e) => {
        addPhotoFiles(Array.from(e.target.files || []));
        e.target.value = '';
    };

    const setPhotoDescription = (key, description) => {
        setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, description } : p)));
    };

    const removePhoto = (key) => {
        setPhotos((prev) => {
            const removed = prev.find((p) => p.key === key);
            if (removed?.file && removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter((p) => p.key !== key);
        });
    };

    const photosUploading = photos.some((p) => p.uploading);

    /* ── Draft persistence ── */
    const buildBody = useCallback((draftStep) => ({
        ...form,
        setup_photos: photos
            .map((p) => (p.file_key ? p : { ...p, ...(uploadsRef.current.done[p.key] || {}) }))
            .filter((p) => p.file_key && p.file_url)
            .map((p) => ({ file_key: p.file_key, file_url: p.file_url, description: p.description.trim(), make_task: !!p.make_task })),
        property_tasks: tasks
            .map((t) => ({ instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }))
            .filter((t) => t.instruction),
        // Only sent when a NON-default entity is chosen — omitting it
        // entirely keeps the body identical for non-multi clients.
        ...(billedTo ? { billing_profile_id: Number(billedTo) } : {}),
        ...(draftStep ? { draft_step: draftStep } : {}),
    }), [form, photos, tasks, billedTo]);

    latestRef.current = { buildBody, draftId, done };

    // Save the whole form onto the draft (creating it the first time).
    const persist = async (completedStep) => {
        const draftStep = Math.max(furthestRef.current, completedStep || 0, 1);
        const body = buildBody(draftStep);
        const res = draftId
            ? await axios.put(`${DRAFTS_URL}/${draftId}`, body)
            : await axios.post(DRAFTS_URL, body);
        const saved = res.data?.request;
        furthestRef.current = draftStep;
        if (saved?.id && !draftId) setDraftId(saved.id);
        if (onSaved && saved) onSaved(saved);
        return saved;
    };

    const stepProblem = (n) => {
        if (n === 1) {
            if (!form.address_line_1.trim() || !form.unit_number.trim() || !form.area) {
                return 'Please fill in the building name, unit number and area.';
            }
            if (position === null) return 'Please pin your building on the map to continue.';
        }
        if (n === 2) {
            if ((Number(form.single_beds) + Number(form.double_beds) + Number(form.super_king_beds)) <= 0) {
                return 'Please enter at least one bed.';
            }
        }
        if (n === 4) {
            if (photosUploading) return 'Your photos are still uploading — one moment.';
            if (photos.some((p) => p.failed)) return 'A photo failed to upload. Remove it and try again.';
        }
        return '';
    };

    const canContinue = !saving && !submitting;

    const handleContinue = async () => {
        const problem = stepProblem(step);
        if (problem) { setAttempted(true); setError(problem); return; }
        setError('');
        setAttempted(false);
        setSaving(true);
        try {
            await persist(step);
            setStep((s) => Math.min(LAST_STEP, s + 1));
        } catch (err) {
            console.error('Error saving property draft:', err);
            if (err.response?.data?.code === 'DRAFT_GONE') {
                setError('This draft no longer exists. Close this window and start again.');
            } else {
                setError(err.response?.data?.error || 'Could not save your progress. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        setError('');
        setAttempted(false);
        setStep((s) => Math.max(1, s - 1));
    };

    const jumpTo = (n) => {
        // Completed steps are revisitable; later ones open through Continue.
        if (n < step) { setError(''); setAttempted(false); setStep(n); }
    };

    const handleSubmit = async () => {
        for (const n of [1, 2, 4]) {
            const problem = stepProblem(n);
            if (problem) { setAttempted(true); setError(problem); setStep(n); return; }
        }
        setSubmitting(true);
        setError('');
        try {
            const body = buildBody(null);
            if (draftId) {
                await axios.post(`${DRAFTS_URL}/${draftId}/submit`, body);
            } else {
                // Should not happen (step 1 creates the draft) — the one-shot
                // route still accepts the same body.
                await axios.post('/api/client-requests/requests/properties', body);
            }
            setDone(true);
            if (onSubmitted) onSubmitted();
        } catch (err) {
            console.error('Error submitting property request:', err);
            const code = err.response?.data?.code;
            if (code === 'ALREADY_SUBMITTED') {
                setDone(true);
                if (onSubmitted) onSubmitted();
            } else if (code === 'DRAFT_GONE') {
                setError('This draft no longer exists. Close this window and start again.');
            } else {
                setError(err.response?.data?.error || 'Failed to submit request. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDiscard = async () => {
        if (!draftId) { onClose(); return; }
        if (!window.confirm('Discard this draft property? Everything you entered for it will be removed.')) return;
        try {
            await axios.delete(`${DRAFTS_URL}/${draftId}`);
        } catch (err) {
            // A draft that is already gone is the outcome we wanted.
            if (err.response?.status !== 404) {
                setError(err.response?.data?.error || 'Could not discard the draft. Please try again.');
                return;
            }
        }
        if (onSaved) onSaved(null);
        onClose();
    };

    // Closing keeps the draft: whatever is on screen is saved onto it
    // (fire-and-forget) so "continue later" continues from here.
    const handleClose = () => {
        const { buildBody: build, draftId: id, done: finished } = latestRef.current;
        if (id && !finished) {
            // A photo still uploading finishes first, so the draft keeps it.
            Promise.allSettled(uploadsRef.current.pending)
                .then(() => axios.put(`${DRAFTS_URL}/${id}`, build(Math.max(furthestRef.current, 1))))
                .then((res) => { if (onSaved && res.data?.request) onSaved(res.data.request); })
                .catch(() => { /* the last Continue already saved the step */ });
        }
        onClose();
    };

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // The standard linen set THIS layout gets — follows the beds and
    // bathrooms as the client changes them (owner, 2026-09-14).
    const includedSet = (
        <IncludedSet
            label="✓ Standard Linen Hire and Amenities always included in All Inclusive Service"
            quantities={{ custom: false, items: standardLinenItems(form) }}
            quantitiesTitle="Your standard linen set, every cleaning"
            quantitiesHint="Based on the layout you selected — it updates as you change beds and bathrooms."
        />
    );

    return (
        <div className="creq-modal-overlay" onClick={handleClose}>
            <div className="creq-modal creq-modal--wide creq-modal--steps" onClick={(e) => e.stopPropagation()}>
                {done ? (
                    <div className="creq-success">
                        <div className="creq-pending-badge">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                        </div>
                        <span className="creq-pending-chip">Pending review</span>
                        <h3>Not active yet</h3>
                        <p>
                            Your property request has been received and is now awaiting our
                            team’s approval. It is not active yet — we’ll notify you the moment
                            it’s approved, and you can track it anytime from your home page.
                        </p>
                        <button className="creq-btn creq-btn--primary" onClick={onClose}>Done</button>
                    </div>
                ) : (
                    <>
                        <div className="creq-modal-header">
                            <div>
                                <h3 className="creq-modal-title-row">
                                    {draft ? 'Continue your property' : 'Add New Property'}
                                    <span className="creq-modal-stepchip">
                                        Step {step} of {LAST_STEP} · {STEPS[step - 1].title}
                                    </span>
                                </h3>
                            </div>
                            <button className="creq-modal-close" onClick={handleClose} title="Close">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="creq-steps" role="list">
                            {STEPS.map((s) => (
                                <button
                                    type="button"
                                    key={s.n}
                                    role="listitem"
                                    className={`creq-step ${s.n === step ? 'is-active' : ''} ${s.n < step ? 'is-done' : ''}`}
                                    onClick={() => jumpTo(s.n)}
                                    disabled={s.n >= step}
                                    aria-current={s.n === step ? 'step' : undefined}
                                    aria-label={`Step ${s.n}: ${s.title}`}
                                    title={s.n < step ? `Back to ${s.title}` : s.title}
                                >
                                    <span className="creq-step-label">
                                        <span className="creq-step-num">{s.n}.</span>
                                        <span className="creq-step-name"> {s.short}</span>
                                    </span>
                                    <span className="creq-step-bar" />
                                </button>
                            ))}
                        </div>

                        <div className="creq-modal-body" ref={bodyRef}>
                            {step === 1 && (
                                <>
                                    <div className="creq-section-label">Location</div>
                                    {GMAPS_KEY ? (
                                        <APIProvider apiKey={GMAPS_KEY}>
                                            <div className="creq-map-block">
                                                <div className="creq-map-search">
                                                    <div className="creq-map-search-input">
                                                        <PlacesSearch onPick={handlePlacePick} initialText={form.google_place_desc || ''} />
                                                    </div>
                                                </div>
                                                <div className="creq-map">
                                                    <Map
                                                        mapId={MAP_ID}
                                                        defaultCenter={position || START_POS}
                                                        defaultZoom={position ? 16 : 11}
                                                        disableDefaultUI={true}
                                                        zoomControl={true}
                                                        gestureHandling="greedy"
                                                    >
                                                        {position && <PanTo position={position} />}
                                                        {position && <AdvancedMarker position={position} />}
                                                        <MapClick onPick={handleMapPick} />
                                                    </Map>
                                                </div>
                                                {position ? (
                                                    <div className="creq-map-hint">
                                                        {`📍 ${form.google_place_desc || `Pinned: ${Number(form.latitude).toFixed(5)}, ${Number(form.longitude).toFixed(5)}`}`}
                                                    </div>
                                                ) : attempted ? (
                                                    <div className="creq-map-hint creq-map-hint--required">
                                                        Required — search above or tap the map to pin your building.
                                                    </div>
                                                ) : (
                                                    <div className="creq-map-hint">
                                                        Search your building — the name and area fill in from the map.
                                                    </div>
                                                )}
                                            </div>
                                        </APIProvider>
                                    ) : (
                                        <label className="creq-label creq-label--wide">
                                            Google Maps link (optional)
                                            <input
                                                type="text" maxLength={300}
                                                placeholder="https://maps.app.goo.gl/…"
                                                value={form.google_maps_link}
                                                onChange={(e) => set({ google_maps_link: e.target.value })}
                                            />
                                        </label>
                                    )}

                                    <div className="creq-grid creq-grid--after-map">
                                        <label className="creq-label">
                                            Building name *
                                            <input
                                                type="text" maxLength={120}
                                                placeholder="e.g. Iris Bay Tower"
                                                value={form.address_line_1}
                                                onChange={(e) => set({ address_line_1: e.target.value })}
                                            />
                                        </label>
                                        <label className="creq-label">
                                            Unit number *
                                            <input
                                                type="text" maxLength={30}
                                                placeholder="e.g. 1706"
                                                value={form.unit_number}
                                                onChange={(e) => set({ unit_number: e.target.value })}
                                            />
                                        </label>
                                        <label className="creq-label creq-label--wide">
                                            Area *
                                            <Select
                                                options={AREA_OPTIONS}
                                                value={AREA_OPTIONS.find((o) => o.value === form.area) || null}
                                                placeholder="Select area"
                                                onChange={(opt) => set({ area: opt ? opt.value : '' })}
                                                {...selectProps}
                                                formatOptionLabel={formatAreaOption}
                                            />
                                        </label>
                                    </div>

                                    {/* ── Billing entity (multi-billing clients only) ── */}
                                    {billingOptions.length > 1 && (
                                        <>
                                            <div className="creq-section-label">Billing</div>
                                            <div className="creq-grid">
                                                <label className="creq-label creq-label--wide">
                                                    Billed to
                                                    <Select
                                                        options={billingOptions}
                                                        value={billingOptions.find((o) => o.value === billedTo) || billingOptions[0]}
                                                        onChange={(opt) => setBilledTo(opt ? opt.value : '')}
                                                        {...selectProps}
                                                        isSearchable={false}
                                                    />
                                                </label>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {step === 2 && (
                                <>
                                    <div className="creq-section-label">Property layout</div>
                                    <div className="creq-grid">
                                        <label className="creq-label">
                                            Bedrooms
                                            <Select
                                                options={BEDROOM_OPTIONS}
                                                value={BEDROOM_OPTIONS.find((o) => o.value === form.bedrooms) || null}
                                                onChange={(opt) => { const bd = opt ? opt.value : 0; set({ bedrooms: bd, required_cleaners: bd >= 3 ? 2 : 1 }); }}
                                                {...selectProps}
                                                isSearchable={false}
                                            />
                                        </label>
                                        <label className="creq-label">
                                            Bathrooms
                                            <Select
                                                options={BATHROOM_OPTIONS}
                                                value={BATHROOM_OPTIONS.find((o) => o.value === form.bathrooms) || null}
                                                onChange={(opt) => set({ bathrooms: opt ? opt.value : 1 })}
                                                {...selectProps}
                                                isSearchable={false}
                                            />
                                        </label>
                                    </div>
                                    <div className="creq-grid creq-grid--3">
                                        <label className="creq-label">
                                            Queen / King Size beds
                                            <Select
                                                options={BED_OPTIONS}
                                                value={BED_OPTIONS.find((o) => o.value === form.double_beds) || null}
                                                onChange={(opt) => set({ double_beds: opt ? opt.value : 0 })}
                                                {...selectProps}
                                                isSearchable={false}
                                            />
                                        </label>
                                        <label className="creq-label">
                                            Super King Size beds
                                            <Select
                                                options={BED_OPTIONS}
                                                value={BED_OPTIONS.find((o) => o.value === form.super_king_beds) || null}
                                                onChange={(opt) => set({ super_king_beds: opt ? opt.value : 0 })}
                                                {...selectProps}
                                                isSearchable={false}
                                            />
                                        </label>
                                        <label className="creq-label">
                                            Single beds
                                            <Select
                                                options={BED_OPTIONS}
                                                value={BED_OPTIONS.find((o) => o.value === form.single_beds) || null}
                                                onChange={(opt) => set({ single_beds: opt ? opt.value : 0 })}
                                                {...selectProps}
                                                isSearchable={false}
                                            />
                                        </label>
                                    </div>

                                    <div className="creq-included-gap">{includedSet}</div>

                                    {Number(form.bedrooms) === 2 && (
                                        <div className="creq-cleaners-row">
                                            <div className="creq-cleaners-label">
                                                Standard cleaner allocation — 2-bedroom apartments can request an extra cleaner for AED 40:
                                            </div>
                                            <div className="creq-segmented">
                                                <button
                                                    type="button"
                                                    className={`creq-segment ${form.required_cleaners < 2 ? 'is-active' : ''}`}
                                                    onClick={() => set({ required_cleaners: 1 })}
                                                >
                                                    <strong>Standard · 1 cleaner</strong>
                                                    <span>4 hours</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`creq-segment ${form.required_cleaners >= 2 ? 'is-active' : ''}`}
                                                    onClick={() => set({ required_cleaners: 2 })}
                                                >
                                                    <strong>Extra cleaner · 2 cleaners</strong>
                                                    <span>2.5 hours · + AED 40</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {step === 3 && (
                                <>
                                    <div className="creq-section-label">Additional Amenities &amp; Services</div>
                                    {includedSet}
                                    <div className="creq-toggle-grid">
                                        <Toggle
                                            checked={form.is_beach_towel}
                                            onChange={(on) => set({ is_beach_towel: on })}
                                            label="Beach towels"
                                            hint="AED 7 per towel"
                                        />
                                        <Toggle
                                            checked={form.is_dolce_gusto}
                                            onChange={(on) => set({ is_dolce_gusto: on })}
                                            label="Dolce Gusto capsules"
                                            hint="4 capsules · AED 8 per bedroom"
                                        />
                                        <Toggle
                                            checked={form.is_nespresso}
                                            onChange={(on) => set({ is_nespresso: on })}
                                            label="Nespresso capsules"
                                            hint="4 capsules · AED 5 per bedroom"
                                        />
                                    </div>
                                    <div className="creq-photos-note creq-photos-note--flush">
                                        Nothing extra needed? Just continue — the standard set is always included.
                                    </div>
                                </>
                            )}

                            {step === 4 && (
                                <>
                                    {/* ── Apartment setup photos ── */}
                                    <div className="creq-section-label">Apartment setup photos (optional)</div>
                                    <div className="creq-photos-note">
                                        If you have pictures showing how areas of the apartment should look
                                        when each guest arrives — or where particular items belong — upload
                                        them here. Add a note to each photo, e.g. “Make sure the decoration
                                        is always on this table” or “The bed is always made like this”.
                                    </div>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={handlePickPhotos}
                                    />

                                    {photos.length > 0 && (
                                        <div className="creq-photos-list">
                                            {photos.map((photo, index) => (
                                                <div className="creq-photo-item" key={photo.key}>
                                                    <img src={photo.previewUrl} alt={`Setup ${index + 1}`} />
                                                    <div className="creq-photo-fields">
                                                        <input
                                                            type="text"
                                                            maxLength={300}
                                                            placeholder="Optional note — e.g. “Decoration always on this table”"
                                                            value={photo.description}
                                                            onChange={(e) => setPhotoDescription(photo.key, e.target.value)}
                                                        />
                                                        {photo.uploading ? (
                                                            <span className="creq-photo-state">Uploading…</span>
                                                        ) : photo.failed ? (
                                                            <span className="creq-photo-state creq-photo-state--failed">Upload failed — remove and try again</span>
                                                        ) : (
                                                            <Toggle
                                                                className="creq-toggle--compact"
                                                                checked={!!photo.make_task}
                                                                onChange={() => togglePhotoTask(photo.key)}
                                                                label="Request cleaner to send photo report at every visit"
                                                                hint="The cleaner photographs this spot on every visit"
                                                            />
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="creq-photo-remove"
                                                        onClick={() => removePhoto(photo.key)}
                                                        title="Remove photo"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {photos.length < 10 && (
                                        <div
                                            className={`creq-dropzone ${dragOver ? 'is-dragover' : ''}`}
                                            onClick={() => fileInputRef.current?.click()}
                                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                            onDragLeave={() => setDragOver(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDragOver(false);
                                                addPhotoFiles(Array.from(e.dataTransfer.files || []));
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <FontAwesomeIcon icon={faCamera} className="creq-dropzone-icon" />
                                            <div className="creq-dropzone-text">
                                                <strong>Drop photos here</strong> or click to browse
                                            </div>
                                            <div className="creq-dropzone-hint">Up to 10 images · notes optional</div>
                                        </div>
                                    )}

                                    {/* ── Tasks (after setup pictures, before notes) ── */}
                                    <div className="creq-section-label">Tasks for every visit (optional)</div>
                                    <div className="creq-photos-note">
                                        Instructions the cleaner should follow on every visit to this property.
                                    </div>
                                    <TaskListEditor
                                        tasks={tasks}
                                        onChange={setTasks}
                                        photoTasks={photos
                                            .filter((p) => p.make_task)
                                            // The local preview is the same picture and always loads; a
                                            // reopened draft has only the stored URL.
                                            .map((p) => ({ id: p.key, instruction: (p.description || '').trim() || 'Take this photo at each cleaning', photo_url: p.previewUrl || p.file_url }))}
                                        addLabel="Add a task for this property"
                                        emptyHint="No tasks yet. Add instructions for every cleaning here."
                                        disabled={submitting}
                                    />
                                </>
                            )}

                            {step === 5 && (
                                <>
                                    {/* ── Access & WiFi (optional) ── */}
                                    <div className="creq-section-label">Access &amp; WiFi (optional)</div>
                                    <div className="creq-grid">
                                        <label className="creq-label creq-label--wide">
                                            How do we access the property?
                                            <input
                                                type="text" maxLength={300}
                                                placeholder="e.g. Key with concierge, smart lock code…"
                                                value={form.property_access}
                                                onChange={(e) => set({ property_access: e.target.value })}
                                            />
                                        </label>
                                        <label className="creq-label">
                                            WiFi name
                                            <input
                                                type="text" maxLength={80}
                                                placeholder="Optional"
                                                value={form.wifi_name}
                                                onChange={(e) => set({ wifi_name: e.target.value })}
                                            />
                                        </label>
                                        <label className="creq-label">
                                            WiFi password
                                            <input
                                                type="text" maxLength={80}
                                                placeholder="Optional"
                                                value={form.wifi_password}
                                                onChange={(e) => set({ wifi_password: e.target.value })}
                                            />
                                        </label>
                                    </div>

                                    {/* ── Notes ── */}
                                    <div className="creq-section-label">Notes (optional)</div>
                                    <label className="creq-label creq-label--wide">
                                        Anything we should know about this property?
                                        <textarea
                                            maxLength={500}
                                            placeholder="Anything we should know about this property…"
                                            value={form.notes}
                                            onChange={(e) => set({ notes: e.target.value })}
                                        />
                                    </label>
                                </>
                            )}

                        {/* ── Price calculator: from step 2 on, scrolls with the form, re-priced live ── */}
                        {step >= 2 && (
                            <div className="creq-price-panel creq-price-panel--steps">
                                    <div className="creq-price-head">
                                        <div>
                                            <div className="creq-price-title">All Inclusive Cleaning Price - Calculator</div>
                                            <div className="creq-price-nothing">Nothing to pay now</div>
                                            <div className="creq-price-sub">
                                                {price
                                                    ? `${price.cleaners === 1 ? '1 cleaner' : `${price.cleaners} cleaners`}`
                                                        + (price.duration_text ? ` · approx. ${price.duration_text.toLowerCase()}` : '')
                                                        + (priceLoading ? ' · updating…' : ' · updates as you go')
                                                    : (priceLoading ? 'Calculating…' : 'Price unavailable right now')}
                                            </div>
                                        </div>
                                        <div className="creq-price-total">{price ? money(price.total_incl_vat) : '—'}</div>
                                    </div>
                                    {price && (
                                        <>
                                            <div className="creq-price-breakdown">
                                                {price.breakdown.map((row) => (
                                                    <div className="creq-price-row" key={row.label}>
                                                        <span>{row.label}</span>
                                                        <span>{money(row.amount)}</span>
                                                    </div>
                                                ))}
                                                <div className="creq-price-row">
                                                    <span>Subtotal</span>
                                                    <span>{money(price.total)}</span>
                                                </div>
                                                <div className="creq-price-row">
                                                    <span>VAT (5%)</span>
                                                    <span>{money(price.vat)}</span>
                                                </div>
                                                <div className="creq-price-row creq-price-row--strong">
                                                    <span>Total incl. VAT</span>
                                                    <span>{money(price.total_incl_vat)}</span>
                                                </div>
                                            </div>
                                            <div className="creq-price-footnote">
                                                Standard Linen Hire and Amenities always included in your All Inclusive price
                                                — this is your price per cleaning, charged per booking.
                                            </div>
                                        </>
                                    )}
                            </div>
                        )}

                            {!!error && <div className="creq-error">{error}</div>}
                        </div>

                        <div className="creq-modal-actions creq-modal-actions--split">
                            <div className="creq-actions-left">
                                {draftId ? (
                                    <button type="button" className="creq-link-danger" onClick={handleDiscard} disabled={saving || submitting}>
                                        Discard draft
                                    </button>
                                ) : (
                                    <span className="creq-draft-note">Your progress is saved as a draft after this step.</span>
                                )}
                            </div>
                            <div className="creq-actions-right">
                                {step === 1 ? (
                                    <button className="creq-btn creq-btn--ghost" onClick={handleClose} disabled={saving}>
                                        Cancel
                                    </button>
                                ) : (
                                    <button className="creq-btn creq-btn--ghost" onClick={handleBack} disabled={saving || submitting}>
                                        Back
                                    </button>
                                )}
                                {step < LAST_STEP ? (
                                    <button
                                        className="creq-btn creq-btn--primary"
                                        onClick={handleContinue}
                                        disabled={!canContinue}
                                    >
                                        {saving ? 'Saving…' : 'Continue'}
                                    </button>
                                ) : (
                                    <button
                                        className="creq-btn creq-btn--primary"
                                        onClick={handleSubmit}
                                        disabled={submitting || saving || photosUploading}
                                    >
                                        {submitting ? 'Submitting…' : 'Submit for review'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
